import React, {useState} from 'react';
import {motion} from 'framer-motion';
import {
    Building2,
    Camera,
    HardDrive,
    Home,
    MonitorCog,
    Plane,
    Server,
    ShieldCheck,
    Wrench
} from 'lucide-react';

type TabType = 'private' | 'business';

const privatePricing = [
    {
        category: 'Kamery i Monitoring', items: [
            {name: 'Montaż kamery zewnętrznej (IP/PoE)', price: 'od 250 zł/szt.'},
            {name: 'Montaż kamery wewnętrznej', price: 'od 200 zł/szt.'},
            {name: 'Konfiguracja rejestratora NVR/DVR', price: 'od 300 zł'},
            {name: 'Podgląd zdalny na telefonie/komputerze', price: '150 zł'},
            {name: 'Kamera obrotowa PTZ, montaż i konfiguracja', price: 'od 450 zł'},
        ]
    },
    {
        category: 'Mocowania i Dedykowane Puszki', items: [
            {name: 'Uchwyt ścienny do kamery (z montażem)', price: 'od 50 zł/szt.'},
            {name: 'Uchwyt słupowy / narożny', price: 'od 80 zł/szt.'},
            {name: 'Dedykowana puszka montażowa (junction box)', price: 'od 45 zł/szt.'},
            {name: 'Maszt / słupek do kamery (do 3m)', price: 'od 250 zł'},
            {name: 'Okablowanie (prowadzenie kabli)', price: 'od 15 zł/mb'},
        ]
    },
    {
        category: 'Domofony i Kontrola Dostępu', items: [
            {name: 'Montaż wideodomofonu', price: 'od 400 zł'},
            {name: 'Montaż domofonu audio', price: 'od 250 zł'},
            {name: 'Zamek elektroniczny z PIN/kartą', price: 'od 350 zł'},
        ]
    },
    {
        category: 'Opieka Techniczna', items: [
            {name: 'Pakiet roczny Basic (1 wizyta + zdalny dostęp)', price: '499 zł/rok'},
            {name: 'Pakiet roczny Standard (2 wizyty + priorytet)', price: '899 zł/rok'},
            {name: 'Jednorazowy serwis / konserwacja', price: 'od 200 zł'},
        ]
    },
];

const businessPricing = [
    {
        category: 'Systemy Monitoringu', items: [
            {name: 'Montaż kamery zewnętrznej IP 4K', price: 'od 350 zł/szt.'},
            {name: 'Montaż kamery wewnętrznej IP', price: 'od 280 zł/szt.'},
            {name: 'System NVR do 32 kamer + konfiguracja', price: 'od 1 500 zł'},
            {name: 'Kamera obrotowa PTZ z analityką', price: 'od 800 zł'},
            {name: 'Integracja z systemem alarmowym', price: 'od 500 zł'},
        ]
    },
    {
        category: 'Mocowania i Infrastruktura', items: [
            {name: 'Uchwyt ścienny / sufitowy (montaż)', price: 'od 50 zł/szt.'},
            {name: 'Uchwyt słupowy / narożny wzmocniony', price: 'od 100 zł/szt.'},
            {name: 'Puszka montażowa', price: 'od 35 zł/szt.'},
            {name: 'Szafka rakowa 9U (z montażem)', price: 'od 950 zł'},
            {name: 'Szafka rakowa 12U (z montażem)', price: 'od 1 500 zł'},
            {name: 'Szafka rakowa 22U+ stojąca (z montażem)', price: 'od 3 000 zł'},
            {name: 'Okablowanie strukturalne (prowadzenie)', price: 'od 12 zł/mb'},
        ]
    },
    {
        category: 'Usługi Informatyczne', items: [
            {name: 'Konsultacje informatyczne', price: '230 zł/godz.'},
            {name: 'Instalacja serwera Windows / Linux', price: 'od 1 000 zł'},
            {name: 'Konfiguracja routera, switcha', price: 'od 150 zł/godz.'},
            {name: 'Administracja serwerami', price: '150 zł/godz.'},
            {name: 'Budowa sieci WiFi z roamingiem', price: 'Do ustalenia'},
            {name: 'Testy penetracyjne', price: 'od 20 000 zł'},
        ]
    },
    {
        category: 'Mapa 3D Dronem', items: [
            {name: 'Przelot dronem i zdjęcia z góry (do 2ha)', price: 'od 1 200 zł'},
            {name: 'Przygotowanie mapy 3D obiektu', price: 'od 2 500 zł'},
            {name: 'Mapa 3D + plan rozmieszczenia kamer', price: 'od 3 500 zł'},
            {name: 'Dokumentacja fotograficzna z drona', price: 'od 800 zł'},
        ]
    },
    {
        category: 'Opieka Techniczna', items: [
            {name: 'Pakiet roczny Firma Basic (kwartalny serwis)', price: '1 499 zł/rok'},
            {name: 'Pakiet roczny Firma Standard (co 2 miesiące)', price: '2 499 zł/rok'},
            {name: 'Pakiet roczny Firma Premium (priorytet 24/7)', price: '4 999 zł/rok'},
            {name: 'Outsourcing IT (stały nadzór)', price: 'Do ustalenia'},
            {name: 'Jednorazowy serwis / naprawa', price: 'od 250 zł'},
        ]
    },
];

const iconMap: Record<string, React.ElementType> = {
    'Kamery i Monitoring': Camera,
    'Mocowania i Dedykowane Puszki': Wrench,
    'Domofony i Kontrola Dostępu': MonitorCog,
    'Opieka Techniczna': ShieldCheck,
    'Systemy Monitoringu': Camera,
    'Mocowania i Infrastruktura': Server,
    'Usługi Informatyczne': HardDrive,
    'Mapa 3D Dronem': Plane,
};

export function PricingSection() {
    const [activeTab, setActiveTab] = useState<TabType>('private');

    const currentPricing = activeTab === 'private' ? privatePricing : businessPricing;

    return (
        <section id="cennik" className="w-full scroll-mt-20 py-20 md:py-28 px-5 bg-muted/50">
            <div className="max-w-6xl mx-auto">
                <motion.div
                    initial={{opacity: 0}}
                    whileInView={{opacity: 1}}
                    viewport={{once: true}}
                    transition={{duration: 0.6}}
                    className="text-center mb-12"
                >
                    <div
                        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-border bg-primary/5 text-xs md:text-sm font-semibold text-primary mb-4">
                        Cennik montażu kamer i usług IT - Mielec i promień do 250 km
                    </div>
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
                        Uczciwe ceny, <span className="gradient-text">zero niespodzianek</span>
                    </h2>
                    <p className="text-muted-foreground max-w-xl mx-auto">
                        Wiesz, za co płacisz. Przejrzyste ceny montażu kamer, monitoringu, alarmów i sieci w&nbsp;Mielcu oraz w promieniu do 250 km.
                        Elastyczne pakiety dla domu i&nbsp;firmy dopasowane do Twojego budżetu.
                    </p>
                </motion.div>

                <motion.div
                    initial={{opacity: 0}}
                    whileInView={{opacity: 1}}
                    viewport={{once: true}}
                    transition={{duration: 0.5, delay: 0.2}}
                    className="flex justify-center mb-12"
                >
                    <div
                        className="inline-flex items-center gap-1 p-1.5 rounded-full bg-muted/60 border border-border/50">
                        <button
                            onClick={() => setActiveTab('private')}
                            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                                activeTab === 'private'
                                    ? 'bg-primary text-white shadow-md shadow-primary/25'
                                    : 'text-muted-foreground hover:text-foreground'
                            }`}
                        >
                            <Home size={16}/>
                            Dla domu
                        </button>
                        <button
                            onClick={() => setActiveTab('business')}
                            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                                activeTab === 'business'
                                    ? 'bg-primary text-white shadow-md shadow-primary/25'
                                    : 'text-muted-foreground hover:text-foreground'
                            }`}
                        >
                            <Building2 size={16}/>
                            Dla firmy
                        </button>
                    </div>
                </motion.div>

                <div className="space-y-8">
                    {currentPricing.map((category, catIdx) => {
                        const CategoryIcon = iconMap[category.category] || Camera;
                        return (
                            <motion.div
                                key={`${activeTab}-${category.category}`}
                                initial={{opacity: 0}}
                                whileInView={{opacity: 1}}
                                viewport={{once: true}}
                                transition={{duration: 0.5, delay: catIdx * 0.1}}
                                className="rounded-2xl border border-border/50 bg-card overflow-hidden"
                            >
                                <div
                                    className="flex items-center gap-3 px-6 py-4 border-b border-border/50 bg-muted/50">
                                    <div
                                        className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                                        <CategoryIcon size={20} className="text-primary"/>
                                    </div>
                                    <h3 className="font-bold text-lg text-foreground">{category.category}</h3>
                                </div>
                                <div className="divide-y divide-border/50">
                                    {category.items.map((item, i) => (
                                        <div
                                            key={i}
                                            className="flex items-center justify-between px-6 py-3.5 hover:bg-muted/50 transition-colors"
                                        >
                                            <span
                                                className="text-sm md:text-base text-foreground/80 pr-4">{item.name}</span>
                                            <span
                                                className="text-sm md:text-base font-semibold text-primary whitespace-nowrap">{item.price}</span>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                <motion.div
                    initial={{opacity: 0}}
                    whileInView={{opacity: 1}}
                    viewport={{once: true}}
                    transition={{duration: 0.5, delay: 0.3}}
                    className="mt-12 text-center"
                >
                    <p className="text-muted-foreground mb-2 text-sm">
                        Dokładna cena zależy od zakresu prac i lokalizacji w Mielcu oraz w promieniu do 250 km. Skontaktuj się po bezpłatną,
                        indywidualną wycenę.
                    </p>
                    <p className="text-muted-foreground/70 mb-4 text-[11px] max-w-2xl mx-auto leading-relaxed">
                        Treści i&nbsp;ceny prezentowane na stronie mają charakter poglądowy i&nbsp;nie stanowią oferty
                        handlowej w&nbsp;rozumieniu art.&nbsp;66 §&nbsp;1 Kodeksu cywilnego. Każda współpraca wymaga
                        indywidualnego potwierdzenia i&nbsp;podpisania umowy lub złożenia zamówienia.
                    </p>
                </motion.div>
            </div>
        </section>
    );
}
